import { normalizeTrackTitle } from "./trackIdentity";

export const TITLE_MATCH_THRESHOLD = 0.8;

const VERSION_TAG_PATTERNS: Array<[string, RegExp]> = [
  ["instrumental", /\binstrumental\b/],
  ["karaoke", /\bkaraoke\b/],
  ["acapella", /\ba ?cap+ella\b/],
  ["remix", /\bremix(?:es|ed)?\b|\bbootleg\b|\brework\b|\bflip\b/],
  ["acoustic", /\bacoustic\b|\bunplugged\b/],
  ["live", /\blive\b/],
  ["sped up", /\bsped ?up\b|\bnightcore\b/],
  ["slowed", /\bslowed\b|\bchopped\b|\bscrewed\b/],
  ["radio edit", /\bradio (?:edit|version)\b/],
  ["extended", /\bextended\b/],
  ["remastered", /\bremaster(?:ed)?\b/],
  ["demo", /\bdemo\b/],
  ["reprise", /\breprise\b/],
  ["cover", /\bcover\b/],
  ["clean", /\bclean\b/],
  ["8d", /\b8d\b/],
];

const RELEASE_SUFFIX = /\s*-\s*(?:single|ep)\s*$/i;
const STOP_WORDS = new Set(["the", "a", "an", "of", "and"]);

const stem = (token: string) => (token.length > 3 && token.endsWith("s") && !token.endsWith("ss") ? token.slice(0, -1) : token);

export function normalizeReleaseTitle(title: string | null | undefined): string {
  return normalizeTrackTitle((title || "").replace(RELEASE_SUFFIX, ""));
}

export function titleVersionTags(title: string | null | undefined): string[] {
  const normalized = normalizeReleaseTitle(title);
  if (!normalized) return [];
  return VERSION_TAG_PATTERNS.filter(([, pattern]) => pattern.test(normalized)).map(([tag]) => tag);
}

export function titleTokens(title: string | null | undefined): string[] {
  let normalized = normalizeReleaseTitle(title);
  if (!normalized) return [];
  for (const [, pattern] of VERSION_TAG_PATTERNS) {
    normalized = normalized.replace(new RegExp(pattern.source, "g"), " ");
  }
  return normalized
    .split(/\s+/)
    .filter((token) => token && !STOP_WORDS.has(token))
    .map(stem);
}

export function versionTagsMatch(requested: string | null | undefined, candidate: string | null | undefined): boolean {
  const left = titleVersionTags(requested);
  const right = new Set(titleVersionTags(candidate));
  return left.length === right.size && left.every((tag) => right.has(tag));
}

export function titleSimilarity(requested: string | null | undefined, candidate: string | null | undefined): number {
  const left = new Set(titleTokens(requested));
  const right = new Set(titleTokens(candidate));
  if (left.size === 0 || right.size === 0) return 0;
  let shared = 0;
  for (const token of left) if (right.has(token)) shared += 1;
  return (2 * shared) / (left.size + right.size);
}

export function titleContainment(requested: string | null | undefined, candidate: string | null | undefined): number {
  const left = new Set(titleTokens(requested));
  const right = new Set(titleTokens(candidate));
  if (left.size === 0 || right.size === 0) return 0;
  let shared = 0;
  for (const token of left) if (right.has(token)) shared += 1;
  return shared / left.size;
}

export function scoreTitleMatch(requested: string | null | undefined, candidate: string | null | undefined): number {
  const left = normalizeReleaseTitle(requested);
  const right = normalizeReleaseTitle(candidate);
  if (!left || !right) return 0;
  if (!versionTagsMatch(requested, candidate)) return -1;
  if (left === right) return 1;
  return titleSimilarity(requested, candidate);
}

export function isTitleMatch(requested: string | null | undefined, candidate: string | null | undefined): boolean {
  return scoreTitleMatch(requested, candidate) >= TITLE_MATCH_THRESHOLD;
}
